import { useLocation } from 'react-router-dom';
import { SectionTabs, type SectionTab } from '../components/SectionTabs';
import { useProfile } from '../profile/ProfileProvider';

/** Absence tool sub-nav — THREE sides (sidebar Personal | Team | Admin
 *  children pick the side; Admin split out Sep 2026):
 *    Personal: My absences · Calendar (own view)
 *    Team:     Approvals · Calendar (team view) · Member view (org-gated)
 *    Admin:    Summaries · Warnings (admins only)
 *  The Approvals queue self-filters, so it's shown to all provisioned users. */
const TEAM_PREFIXES = ['/absence/approvals', '/absence/team-calendar', '/absence/member'];
const ADMIN_PREFIXES = ['/absence/summaries', '/absence/warnings'];

export function absenceSideOf(pathname: string): 'personal' | 'team' | 'admin' {
  if (ADMIN_PREFIXES.some((p) => pathname.startsWith(p))) return 'admin';
  return TEAM_PREFIXES.some((p) => pathname.startsWith(p)) ? 'team' : 'personal';
}

export function AbsenceSection() {
  const { state } = useProfile();
  const { pathname } = useLocation();
  const isAdmin = state.status === 'ready' && state.isAdmin;
  const side = absenceSideOf(pathname);
  const tabs: SectionTab[] =
    side === 'admin' && isAdmin
      ? [
          { to: '/absence/summaries', label: 'Summaries' },
          { to: '/absence/warnings', label: 'Warnings' },
        ]
      : side !== 'personal'
      ? [
          { to: '/absence/approvals', label: 'Approvals' },
          { to: '/absence/team-calendar', label: 'Calendar' },
          { to: '/absence/member', label: 'Member view' },
        ]
      : [
          { to: '/absence', label: 'My absences', end: true },
          { to: '/absence/calendar', label: 'Calendar' },
        ];
  return <SectionTabs title="Absence Management" tabs={tabs} />;
}

const STATUS_STYLES: Record<string, string> = {
  submitted: 'bg-warning-subtle text-warning',
  approved: 'bg-success-subtle text-success',
  rejected: 'bg-danger-subtle text-danger',
  cancelled: 'bg-surface-2 text-fg-faint',
};

export function AbsenceStatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-medium tracking-tighter ${
        STATUS_STYLES[status] ?? 'bg-surface-2 text-fg-faint'
      }`}
    >
      {status}
    </span>
  );
}

export function formatRange(startDate: string, endDate: string): string {
  return startDate === endDate ? startDate : `${startDate} → ${endDate}`;
}
